import { defineStore } from "pinia";
import { entityStoreFactory } from "@/stores/factory/entity-store-factory";
import { computed, ref } from "vue";
import { ChatScene } from "@/types/chat";
import {
  createOneChatScene,
  deleteOneChatScene,
  readChatScene,
  readChatScenePage,
  updateOneChatScene,
} from "@/apis/chatScene";

const PER_PAGE = 999;

export const useChatScene = defineStore("chatScene", () => {
  const entityStore = entityStoreFactory<ChatScene>("id");
  const { updateMap, getEntity } = entityStore;
  const mateId2ChatSceneIdsList = ref<Record<number, number[]>>({});
  const mateId2ChatSceneList = computed(() => {
    const res: Record<number, ChatScene[]> = {};
    for (const mateId in mateId2ChatSceneIdsList.value) {
      res[mateId] = mateId2ChatSceneIdsList.value[mateId].map(
        (id) => getEntity(id)!
      );
    }
    return res;
  });
  const genericChatSceneIdList = ref<number[]>([]);
  const genericChatSceneList = computed(() =>
    genericChatSceneIdList.value.map((id) => getEntity(id)!)
  );

  const getChatSceneList = (mateId: number) =>
    mateId2ChatSceneList.value[mateId] ?? [];

  // Actions
  const fetchChatSceneList = async (mateId: number) => {
    const { page } = await readChatScenePage({
      filter: { mateId },
      pagination: { page: 1, perPage: PER_PAGE },
      sort: { field: "dtUpdate", order: "desc" },
    });
    const chatScenes = updateMap(page.data);
    mateId2ChatSceneIdsList.value[mateId] = chatScenes.map((item) => item.id!);
  };

  const fetchChatScene = async (id: number) => {
    const res = await readChatScene(id);
    return updateMap([res.item]);
  };

  const getOrFetchChatSceneList = async (mateId: number, force = false) => {
    if (!force && getChatSceneList(mateId).length > 0) {
      return mateId2ChatSceneList.value;
    } else {
      return await fetchChatSceneList(mateId);
    }
  };

  const createChatScene = async (
    params: Parameters<typeof createOneChatScene>[0],
    media?: Media
  ) => {
    const res = await createOneChatScene(params);
    const chatScene = res.item;
    chatScene.media = media;
    const val = updateMap([chatScene]);
    mateId2ChatSceneIdsList.value[params.mateId].unshift(chatScene.id!);
    return val[0];
  };

  const deleteChatScene = async (id: number) => {
    await deleteOneChatScene(id);
    const chatScene = getEntity(id)!;
    mateId2ChatSceneIdsList.value[chatScene.mateId] =
      mateId2ChatSceneIdsList.value[chatScene.mateId].filter(
        (item) => item !== id
      );
  };

  const updateChatScene = async (
    id: number,
    params: Parameters<typeof updateOneChatScene>[1],
    media?: Media
  ) => {
    const res = await updateOneChatScene(id, params);
    const chatScene = res.item;
    chatScene.media = media;
    updateMap([chatScene]);
    return chatScene;
  };

  const fetchGenericChatSceneList = async () => {
    const { page } = await readChatScenePage({
      filter: { isCommon: 1 },
      pagination: { page: 1, perPage: PER_PAGE },
      sort: { field: "dtUpdate", order: "desc" },
    });
    const chatScenes = updateMap(page.data);
    genericChatSceneIdList.value = chatScenes.map((item) => item.id!);
  };

  const getOrFetchGenericChatSceneList = async (force = false) => {
    if (!force && genericChatSceneIdList.value.length) {
      return genericChatSceneList.value;
    } else {
      return await fetchGenericChatSceneList();
    }
  };

  const getOrFetchChatScene = async (
    mateId: number,
    id: number,
    force = false
  ) => {
    if (!force && getEntity(id)) {
      return getEntity(id);
    } else {
      await fetchChatSceneList(mateId);
      return getEntity(id);
    }
  };

  const disableCache = () => {
    mateId2ChatSceneIdsList.value = {};
  };

  const isSaving = ref(false);
  let savingCount = 0;
  const openSaving = () => {
    if (!isSaving.value) isSaving.value = true;
    savingCount++;
  };

  const closeSaving = () => {
    savingCount--;
    if (savingCount === 0) isSaving.value = false;
  };

  const withSaving = (fn: () => Promise<any>, id?: number) => {
    openSaving();
    fn().finally(async () => {
      if (id) await fetchChatScene(id);
      closeSaving();
    });
  };

  return {
    // state
    mateId2ChatSceneList,
    genericChatSceneList,
    isSaving,
    withSaving,
    // actions
    getOrFetchChatSceneList,
    createChatScene,
    deleteChatScene,
    updateChatScene,
    disableCache,
    getChatSceneList,
    getOrFetchGenericChatSceneList,
    getOrFetchChatScene,
    getEntity,
    updateMap,
  };
});
